<script setup lang="ts">
import { z } from "zod";
import { maskito as vMaskito } from "@maskito/vue";
import type { MaskitoOptions } from "@maskito/core";
import { licenseNumberRegex } from "@carnet/api/auth";

definePageMeta({
  layout: "auth",
  name: "login",
  middleware: ["unauthenticated"],
});

const { t } = useI18n();
const localePath = useLocalePath();

useSeoMeta({
  title: () => t("login.title"),
  description: () => t("seo.login.description"),
});

const { fetch: fetchSession } = useUserSession();
const route = useRoute();

const credentials = reactive({
  identifier: "",
  password: "",
});

const schema = z.object({
  identifier: z.string().min(1, t("form.validation.identifier_required")).regex(licenseNumberRegex, t("form.validation.identifier_format")),
  password: z.string().min(1, t("form.validation.password_required")),
});

type ErrorKind =
  | "invalid_credentials"
  | "invalid_client"
  | "invalid_request"
  | "sso_provider_not_configured"
  | "server_error"
  | "generic_error"
  | "network_error";

type LoginErrorBody = {
  statusMessage?: unknown;
  data?: { provider?: unknown; missingVariables?: unknown };
};

const generalErrorMessage = ref<string | null>(null);
const errorKind = ref<ErrorKind | null>(null);

const isLoading = ref(false);

async function readErrorBody(response: Response): Promise<LoginErrorBody | null> {
  try {
    return (await response.clone().json()) as LoginErrorBody;
  } catch {
    return null;
  }
}

function readErrorCode(body: LoginErrorBody | null): string | null {
  return typeof body?.statusMessage === "string" ? body.statusMessage : null;
}

function readMissingVariables(body: LoginErrorBody | null): string {
  const missingVariables = body?.data?.missingVariables;
  return Array.isArray(missingVariables) ? missingVariables.filter((name) => typeof name === "string").join(", ") : "";
}

function errorMessage(kind: ErrorKind, body: LoginErrorBody | null): string {
  if (kind !== "sso_provider_not_configured") return t(`auth.errors.${kind}`);
  return t("auth.errors.sso_provider_not_configured", {
    provider: String(body?.data?.provider ?? "?"),
    variables: readMissingVariables(body) || "?",
  });
}

function mapErrorKind(status: number, oauth2Code: string | null): ErrorKind {
  if (oauth2Code === "sso_provider_not_configured") return "sso_provider_not_configured";
  if (oauth2Code === "invalid_grant") return "invalid_credentials";
  if (oauth2Code === "invalid_client" || oauth2Code === "unauthorized_client") {
    return "invalid_client";
  }
  if (status === 401) return "invalid_credentials";
  if (status === 400) return "invalid_request";
  if (status >= 500) return "server_error";
  return "generic_error";
}

async function onSubmit() {
  try {
    generalErrorMessage.value = null;
    errorKind.value = null;
    isLoading.value = true;

    // Use native fetch to ensure cookies are properly handled
    const response = await fetch("/api/login", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(credentials),
    });

    if (!response.ok) {
      const errorBody = await readErrorBody(response);
      const oauth2Code = readErrorCode(errorBody);
      console.error("Login failed:", response.status, response.statusText, oauth2Code, errorBody?.data);

      const kind = mapErrorKind(response.status, oauth2Code);
      errorKind.value = kind;
      generalErrorMessage.value = errorMessage(kind, errorBody);
      return;
    }

    await fetchSession();
    await navigateTo(extractSafeRedirect(route.query) ?? localePath({ name: "index" }));
  } catch (e) {
    console.error("Login error:", e);
    errorKind.value = "network_error";
    generalErrorMessage.value = t("auth.errors.network_error");
  } finally {
    isLoading.value = false;
  }
}

const identifierMaskOptions: MaskitoOptions = {
  mask: [...Array(1).fill(/\w/), "-", ...Array(2).fill(/\w/), "-", ...Array(7).fill(/\w/)],
  preprocessors: [
    ({ elementState, data }) => {
      const { value, selection } = elementState;
      return {
        elementState: {
          selection,
          value: value.toUpperCase().replace(" ", "-"),
        },
        data: data.toUpperCase().replace(" ", "-"),
      };
    },
  ],
};
</script>

<template>
  <AppAuthFormLayout :title="$t('login.title')" :description="$t('login.description')">
    <AppForm
      id="login-form"
      data-testid="login-form"
      :schema
      :state="credentials"
      class="not-last:mb-8 flex flex-col w-full"
      :validate-on="[]"
      @submit="onSubmit"
    >
      <div class="max-sm:grow not-last:mb-8">
        <div class="not-last:mb-10 flex flex-col gap-8">
          <UFormField name="identifier" :label="$t('login.identifier.label')" data-testid="identifier-field">
            <template #hint>
              <AppInputIdentifierHelper />
            </template>
            <UInput
              v-model="credentials.identifier"
              v-maskito="identifierMaskOptions"
              variant="soft"
              data-testid="identifier-input"
              :placeholder="$t('login.identifier.placeholder')"
              autocomplete="username"
            />
          </UFormField>
          <UFormField name="password" :label="$t('login.password.label')" data-testid="password-field">
            <AppInputPassword
              v-model="credentials.password"
              variant="soft"
              type="password"
              data-testid="password-input"
              autocomplete="current-password"
            />
          </UFormField>
        </div>
        <Transition name="fade">
          <AppMessageWrapper v-if="generalErrorMessage" data-testid="error-message" :data-error-kind="errorKind ?? undefined">
            {{ generalErrorMessage }}
          </AppMessageWrapper>
        </Transition>
      </div>
    </AppForm>
    <template #actions>
      <AppLoadingButton
        block
        hide-text-on-load
        type="submit"
        form="login-form"
        data-testid="submit-button"
        color="secondary"
        :loading="isLoading"
      >
        {{ $t("login.submit") }}
      </AppLoadingButton>
      <div class="flex justify-center">
        <UButton variant="link" :to="$localePath({ name: 'password.reset' })">{{ $t("login.reset") }}</UButton>
      </div>
    </template>
  </AppAuthFormLayout>
</template>
